import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, Input, Button } from "@alchema/ui";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { EmptyState } from "~/components/shared/EmptyState";
import * as m from "~/paraglide/messages";

/* ── Types ── */

interface PortalCompany {
  name: string;
  slug: string;
  careersUrl: string;
  apiUrl?: string;
  platform: "greenhouse" | "ashby" | "lever" | "workday" | "custom";
  region: "global" | "dach" | "eu" | "us";
  enabled: boolean;
}

interface ScanResult {
  company: string;
  title: string;
  url: string;
  source: string;
  matchedFilter: boolean;
}

/* ── Route ── */

export const Route = createFileRoute("/portal-scanner")({
  beforeLoad: async () => {
    await requireAuth();
  },
  component: PortalScannerPage,
  pendingComponent: PortalScannerSkeleton,
  errorComponent: RouteErrorBoundary,
});

/* ── Skeleton ── */

function PortalScannerSkeleton() {
  return (
    <div className="mx-auto max-w-6xl" aria-busy="true" role="status" aria-label={m.portal_scanner_loading_aria()}>
      <Skeleton className="h-7 w-56" />
      <Skeleton className="mt-1.5 h-4 w-80" />
      <div className="mt-8 overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-4 px-4 py-3" style={{ borderTop: i > 1 ? "1px solid var(--border-subtle)" : undefined }}>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Constants ── */

const PLATFORMS = ["all", "greenhouse", "ashby", "lever", "workday", "custom"] as const;
const REGIONS = ["all", "global", "dach", "eu", "us"] as const;

/* ── Status dot config ── */

type PortalStatus = "active" | "syncing" | "error" | "inactive";

function getPortalStatus(company: PortalCompany): PortalStatus {
  if (!company.enabled) return "inactive";
  if (company.platform === "greenhouse") return "active";
  return "active";
}

const STATUS_COLORS: Record<PortalStatus, string> = {
  active: "var(--success)",
  syncing: "var(--warning)",
  error: "var(--error)",
  inactive: "var(--text-muted)",
};

const STATUS_LABELS: Record<PortalStatus, () => string> = {
  active: () => m.portal_scanner_enabled(),
  syncing: () => "Syncing",
  error: () => "Error",
  inactive: () => m.portal_scanner_disabled(),
};

/* ── Status Dot ── */

function StatusDot({ status }: { status: PortalStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
      <span
        className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: STATUS_COLORS[status] }}
        aria-hidden="true"
      />
      {STATUS_LABELS[status]()}
    </span>
  );
}

/* ── Filter Tab ── */

function FilterTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-150"
      style={{
        backgroundColor: active ? "var(--accent-subtle)" : "transparent",
        color: active ? "var(--accent-text)" : "var(--text-muted)",
        border: active ? "1px solid var(--accent-subtle)" : "1px solid transparent",
      }}
    >
      {label}
    </button>
  );
}

/* ── Portal Table ── */

function PortalTable({
  companies,
  scanningSlug,
  onScan,
  scanResults,
}: {
  companies: PortalCompany[];
  scanningSlug: string | null;
  onScan: (slug: string) => void;
  scanResults: ScanResult[];
}) {
  const resultsByCompany = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of scanResults) {
      map.set(r.company, (map.get(r.company) ?? 0) + 1);
    }
    return map;
  }, [scanResults]);

  if (companies.length === 0) {
    return (
      <EmptyState
        icon={<RadarIcon />}
        title={m.portal_scanner_empty_title()}
        description={m.portal_scanner_empty_description()}
      />
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: "var(--surface-raised)", borderBottom: "1px solid var(--border-subtle)" }}>
              <th className="section-label px-4 py-2.5 text-left">{m.portal_scanner_col_company()}</th>
              <th className="section-label px-4 py-2.5 text-left">Status</th>
              <th className="section-label px-4 py-2.5 text-left">Platform</th>
              <th className="section-label px-4 py-2.5 text-left">Region</th>
              <th className="section-label px-4 py-2.5 text-right">{m.portal_scanner_col_match()}</th>
              <th className="section-label px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company, idx) => {
              const status = getPortalStatus(company);
              const resultCount = resultsByCompany.get(company.name) ?? 0;
              const isGreenhouse = company.platform === "greenhouse";
              const isScanning = scanningSlug === company.slug;

              return (
                <tr
                  key={company.slug}
                  className="transition-colors duration-100 hover:bg-[var(--bg-secondary)]"
                  style={{
                    borderTop: idx > 0 ? "1px solid var(--border-subtle)" : undefined,
                    opacity: company.enabled ? 1 : 0.55,
                  }}
                >
                  <td className="px-4 py-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "var(--text)" }}
                    >
                      {company.name}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusDot status={status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                      {company.platform.charAt(0).toUpperCase() + company.platform.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>
                      {company.region}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {resultCount > 0 ? (
                      <span
                        className="text-xs font-medium tabular-nums"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {resultCount}
                      </span>
                    ) : (
                      <span style={{ color: "var(--border-strong)" }}>—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isGreenhouse && company.enabled ? (
                      <button
                        onClick={() => onScan(company.slug)}
                        disabled={isScanning}
                        className="text-xs font-medium transition-colors duration-150"
                        style={{
                          color: isScanning ? "var(--text-muted)" : "var(--accent)",
                          cursor: isScanning ? "not-allowed" : "pointer",
                        }}
                      >
                        {isScanning ? m.portal_scanner_scanning() : m.portal_scanner_fetch_jobs()}
                      </button>
                    ) : (
                      <a
                        href={company.careersUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs transition-colors duration-150 hover:underline"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {m.portal_scanner_view_link()}
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Scan Results List ── */

function ScanResultsList({ results }: { results: ScanResult[] }) {
  if (results.length === 0) return null;

  const matched = results.filter((r) => r.matchedFilter).length;
  const total = results.length;

  return (
    <div>
      {/* Stats row */}
      <div className="mb-3 flex items-center gap-4 text-xs" style={{ color: "var(--text-muted)" }}>
        <span>
          <span className="font-medium tabular-nums" style={{ color: "var(--text)" }}>{total}</span>
          {" "}{m.portal_scanner_total_found()}
        </span>
        <span>
          <span className="font-medium tabular-nums" style={{ color: "var(--success)" }}>{matched}</span>
          {" "}{m.portal_scanner_matched()}
        </span>
        <span>
          <span className="font-medium tabular-nums" style={{ color: "var(--error)" }}>{total - matched}</span>
          {" "}{m.portal_scanner_filtered_out()}
        </span>
      </div>

      {/* Results table */}
      <div
        className="overflow-hidden rounded-lg border"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: "var(--surface-raised)", borderBottom: "1px solid var(--border-subtle)" }}>
                <th className="section-label px-4 py-2.5 text-left">{m.portal_scanner_col_title()}</th>
                <th className="section-label px-4 py-2.5 text-left">{m.portal_scanner_col_company()}</th>
                <th className="section-label px-4 py-2.5 text-left">{m.portal_scanner_col_source()}</th>
                <th className="section-label px-4 py-2.5 text-center">{m.portal_scanner_col_match()}</th>
                <th className="section-label px-4 py-2.5 text-right">{m.portal_scanner_col_link()}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, idx) => (
                <tr
                  key={`${result.company}-${result.title}-${idx}`}
                  className="transition-colors duration-100 hover:bg-[var(--bg-secondary)]"
                  style={{ borderTop: idx > 0 ? "1px solid var(--border-subtle)" : undefined }}
                >
                  <td className="px-4 py-2.5">
                    <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                      {result.title}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {result.company}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                      {result.source}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {result.matchedFilter ? (
                      <span aria-label={m.portal_scanner_matched()}>
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: "var(--success)" }}
                        />
                      </span>
                    ) : (
                      <span aria-label={m.portal_scanner_filtered_out()}>
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: "var(--error)" }}
                        />
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium underline-offset-2 hover:underline"
                      style={{ color: "var(--accent)" }}
                    >
                      {m.portal_scanner_view_link()}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Icons ── */

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function RadarIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19.07 4.93A10 10 0 0 0 2 12a10 10 0 0 0 17.07 7.07" />
      <path d="M15.54 8.46A5 5 0 0 0 7 12a5 5 0 0 0 8.54 3.54" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

/* ── Title Filter Tester ── */

function TitleFilterTester() {
  const [testTitle, setTestTitle] = useState("");
  const filterMutation = useMutation({
    ...orpc.portalScanner.filterTitle.mutationOptions(),
  });

  const handleTest = () => {
    if (!testTitle.trim()) return;
    filterMutation.mutate({ title: testTitle.trim() });
  };

  return (
    <div
      className="rounded-lg border p-4"
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface)" }}
    >
      <p className="section-label mb-3">
        {m.portal_scanner_title_filter_test()}
      </p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--text-muted)" }}
          >
            <SearchIcon />
          </div>
          <Input
            value={testTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestTitle(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleTest()}
            placeholder={m.portal_scanner_filter_placeholder()}
            className="pl-9"
          />
        </div>
        <Button
          onClick={handleTest}
          disabled={!testTitle.trim() || filterMutation.isPending}
          style={{ backgroundColor: "var(--accent)", color: "#fff" }}
        >
          {filterMutation.isPending ? m.portal_scanner_testing() : m.portal_scanner_test()}
        </Button>
      </div>
      {filterMutation.data !== undefined && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          {(filterMutation.data as { matched?: boolean })?.matched ? (
            <>
              <span style={{ color: "var(--success)" }}><CheckIcon /></span>
              <span style={{ color: "var(--success)" }}>{m.portal_scanner_filter_pass()}</span>
            </>
          ) : (
            <>
              <span style={{ color: "var(--error)" }}><XIcon /></span>
              <span style={{ color: "var(--error)" }}>{m.portal_scanner_filter_fail()}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */

function PortalScannerPage() {
  const queryClient = useQueryClient();
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [scanningSlug, setScanningSlug] = useState<string | null>(null);

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    ...orpc.portalScanner.listCompanies.queryOptions({ input: undefined }),
    retry: 1,
  });

  const { data: titleFilter } = useQuery({
    ...orpc.portalScanner.getTitleFilter.queryOptions({ input: undefined }),
    retry: 1,
  });

  const scanAllMutation = useMutation(orpc.portalScanner.scanAll.mutationOptions());
  const fetchGreenhouseMutation = useMutation(orpc.portalScanner.fetchGreenhouse.mutationOptions());

  const handleScanAll = () => {
    scanAllMutation.mutate({}, {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          setScanResults(data as ScanResult[]);
        }
      },
    });
  };

  const handleFetchGreenhouse = (slug: string) => {
    setScanningSlug(slug);
    fetchGreenhouseMutation.mutate({ slug }, {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          setScanResults((prev) => {
            const newResults = data as ScanResult[];
            const existingKeys = new Set(
              prev.map((r) => `${r.company}|${r.title}|${r.url}`)
            );
            const deduped = newResults.filter(
              (r) => !existingKeys.has(`${r.company}|${r.title}|${r.url}`)
            );
            return [...prev, ...deduped];
          });
        }
        setScanningSlug(null);
      },
      onError: () => {
        setScanningSlug(null);
      },
    });
  };

  const handleScanSingle = (slug: string) => {
    handleFetchGreenhouse(slug);
  };

  const filteredCompanies = useMemo(() => {
    const typed = companies as PortalCompany[];
    return typed.filter((c) => {
      if (platformFilter !== "all" && c.platform !== platformFilter) return false;
      if (regionFilter !== "all" && c.region !== regionFilter) return false;
      return true;
    });
  }, [companies, platformFilter, regionFilter]);

  const companyCount = (companies as PortalCompany[]).length;

  return (
    <div className="mx-auto max-w-6xl animate-fade-up">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1
            className="font-semibold tracking-tight"
            style={{
              fontSize: "1.375rem",
              fontFamily: "var(--font-display)",
              color: "var(--text)",
            }}
          >
            {m.portal_scanner_page_title()}
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
            {m.portal_scanner_subtitle({ count: companyCount })}
            {titleFilter && (
              <span> · {m.portal_scanner_filter_active()}</span>
            )}
          </p>
        </div>
        <Button
          onClick={handleScanAll}
          disabled={scanAllMutation.isPending}
          className="flex items-center gap-1.5 shrink-0"
          style={{
            backgroundColor: scanAllMutation.isPending ? "var(--bg-secondary)" : "var(--accent)",
            color: scanAllMutation.isPending ? "var(--text-muted)" : "#fff",
            fontSize: "0.8125rem",
            fontWeight: 500,
            padding: "0.5rem 1rem",
          }}
        >
          {scanAllMutation.isPending ? (
            <>
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
              {m.portal_scanner_scanning()}
            </>
          ) : (
            <>
              <RadarIcon />
              {m.portal_scanner_scan_all()}
            </>
          )}
        </Button>
      </div>

      {/* ── Filters ── */}
      <div className="mt-5 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="section-label mr-1">{m.portal_scanner_platform()}</span>
          {PLATFORMS.map((p) => (
            <FilterTab
              key={p}
              label={p === "all" ? m.portal_scanner_all() : p.charAt(0).toUpperCase() + p.slice(1)}
              active={platformFilter === p}
              onClick={() => setPlatformFilter(p)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="section-label mr-1">{m.portal_scanner_region()}</span>
          {REGIONS.map((r) => (
            <FilterTab
              key={r}
              label={r === "all" ? m.portal_scanner_all() : r.toUpperCase()}
              active={regionFilter === r}
              onClick={() => setRegionFilter(r)}
            />
          ))}
        </div>
      </div>

      {/* ── Portal Table ── */}
      <div className="mt-7">
        <p className="section-label mb-2.5">
          {m.portal_scanner_companies_heading()} ({filteredCompanies.length})
        </p>
        {companiesLoading ? (
          <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3" style={{ borderTop: i > 1 ? "1px solid var(--border-subtle)" : undefined }}>
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <PortalTable
            companies={filteredCompanies}
            scanningSlug={scanningSlug}
            onScan={handleScanSingle}
            scanResults={scanResults}
          />
        )}
      </div>

      {/* ── Title Filter Tester ── */}
      <div className="mt-8">
        <TitleFilterTester />
      </div>

      {/* ── Scan Results ── */}
      <div className="mt-8">
        <p className="section-label mb-3">
          {m.portal_scanner_scan_results()}
        </p>

        {scanResults.length === 0 ? (
          <EmptyState
            icon={<RadarIcon />}
            title={m.portal_scanner_empty_title()}
            description={m.portal_scanner_empty_description()}
          />
        ) : (
          <ScanResultsList results={scanResults} />
        )}
      </div>

      {/* bottom spacing */}
      <div className="h-12" />
    </div>
  );
}
